import { ThingFlag } from '@fs/zion-icon'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryParams } from '@fs/zion-router'
import { Card, Image, Paragraph } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { usePageView } from '@fs/zion-analytics'
import usePerson from '../../hooks/usePerson'
import { getMostRecentlyViewed } from '../../services/mostRecentlyViewedService'
import { PERSON_ID_CONTEXT } from '../app/LookupContext'
import { dmcRed, redFlagStyle } from '../../util/CssUtil'
import SubnavAndRoutes from '../app/SubnavAndRoutes'
import MetaData from '../metadata/MetaData'
import RelationshipCalculatorProvider, {
  initialRelCalculatorContext,
} from '../relationshipCalculator/RelationshipCalculatorContext'
import PersonAccessControl from './accesscontrol/PersonAccessControl'
import PersonLabels from './labels/PersonLabels'
import PersonChanges from './changes/PersonChanges'
import PersonMemories from './memories/PersonMemories'
import MergeHistory from './merges/MergeHistory'
import PersonOrdinancesBase from './ordinances/PersonOrdinancesBase'
import OtherInfo from './other/OtherInfo'
import PersonContextProvider from './PersonContext'
import PersonCopies from './copies/PersonCopies'
import PersonRelationshipCalculator from './PersonRelationshipCalculator'
import PersonSources from './sources/PersonSources'
import PersonRelationships from './relationships/PersonRelationships'
import PersonReservations from './reservations/PersonReservations'
import PersonSummary from './summary/PersonSummary'
import picture from '../../assets/jesus.png'
import PersonReservationsHistory from './reservationshistory/PersonReservationsHistory'
import CmisReferences from '../common/cmisrefs/CmisReferences'
import ReservationAuthorizationInfo from '../reservation/ReservationAuthorizationInfo'
import { usePageTitle } from '../app/PageTitleContext'

export default function PersonBase({ match, history }) {
  usePageView({ page_detail: 'DMConsole:PersonBase' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.person.base'))
  const queryParams = useQueryParams()
  const forward = queryParams?.query?.forward

  const personId = match.params.personId
  if (!personId) {
    const mostRecentLookup = getMostRecentlyViewed(PERSON_ID_CONTEXT)
    if (mostRecentLookup && mostRecentLookup !== '') {
      history.push(`/person/${mostRecentLookup}/summary`)
    }
  }

  const { currentPerson, fetchCurrentPerson, getPersonMissingRelationshipOwnerInfo } = usePerson(personId, forward)
  const personContext = { currentPerson, fetchCurrentPerson, getPersonMissingRelationshipOwnerInfo }
  const imageStyle = css`
    display: block;
    margin-inline-start: auto;
    margin-inline-end: auto;
    max-width: 430px;
  `

  const redFlag = () => <ThingFlag size="xs" color={dmcRed} />
  let accessControlStyle = {}
  let accessControlFlag = ''
  let labelStyle = {}
  let labelFlag = ''
  if (currentPerson !== undefined && currentPerson !== null) {
    accessControlStyle = currentPerson.hasAccessControl ? redFlagStyle : null
    labelStyle = currentPerson.hasAdminLabel ? redFlagStyle : null
    accessControlFlag = currentPerson.hasAccessControl ? redFlag() : ''
    labelFlag = currentPerson.hasAdminLabel ? redFlag() : ''
  }

  const subNavs = [
    {
      text: t('summary', 'Summary'),
      path: 'summary',
      requiredPermissions: 'FtLoginPermission',
      component: PersonSummary,
    },
    {
      text: t('person.subnav.relationships', 'Relationships'),
      path: 'relationships',
      requiredPermissions: 'FtLoginPermission',
      component: PersonRelationships,
    },
    {
      text: t('changes.label', 'Changes'),
      path: 'changes',
      requiredPermissions: 'FtLoginPermission',
      component: PersonChanges,
    },
    {
      text: (
        <span>
          <span style={accessControlStyle}>{t('accessControl.label', 'Access Control')}</span>
          {accessControlFlag}
        </span>
      ),
      path: 'accesscontrol',
      requiredPermissions: ['FtLoginPermission', 'ViewCtAccessControlPermission'],
      component: PersonAccessControl,
    },
    {
      text: t('ordinances.label', 'Ordinances'),
      path: 'ordinances',
      requiredPermissions: [
        'FtLoginPermission',
        'ViewOrdinanceSourceReferencePermission',
        'ViewAnyOrdinanceReservationPermission',
        'tss_ReadCtrControlledPermission',
        'tss_ReadCtrFullAcdPermission',
        'ViewOrdinanceSourceReferencePermission',
      ],
      component: PersonOrdinancesBase,
    },
    {
      text: t('reservations.label', 'Reservations'),
      path: 'reservations',
      requiredPermissions: 'FtLoginPermission',
      component: PersonReservations,
    },
    {
      text: t('reservationsHistory.label', 'Reservations History'),
      path: 'reservations-history',
      requiredRoles: ['Data Admin 6', 'Reservation Team Engineer'],
      component: PersonReservationsHistory,
    },
    {
      path: 'reservations-authorization/:authorizationId',
      requiredRoles: ['Data Admin 6', 'Reservation Team Engineer'],
      component: ReservationAuthorizationInfo,
    },
    {
      text: t('person.subnav.sources', 'Sources'),
      path: 'sources',
      requiredPermissions: 'FtLoginPermission',
      component: PersonSources,
    },
    {
      text: (
        <span>
          <span style={labelStyle}> {t('labels.labels', 'Labels')}</span>
          {labelFlag}
        </span>
      ),
      path: 'labels',
      activeStyle: labelStyle,
      requiredPermissions: ['FtLoginPermission', 'ViewAdminLabelPermission'],
      component: PersonLabels,
    },
    {
      text: t('person.subnav.memories', 'Memories'),
      path: 'memories',
      requiredPermissions: 'FtLoginPermission',
      component: PersonMemories,
    },
    {
      text: t('person.subnav.merges', 'Merges'),
      path: 'merges',
      requiredPermissions: 'FtLoginPermission',
      component: MergeHistory,
    },
    {
      text: t('person.subnav.copies', 'Copies'),
      path: 'copies',
      requiredPermissions: 'FtLoginPermission',
      component: PersonCopies,
    },
    {
      text: t('relationshipCalculator.label', 'Relationship Calculator'),
      path: 'relationshipcalculator',
      requiredPermissions: 'FtLoginPermission',
      component: PersonRelationshipCalculator,
    },
    {
      text: t('references', 'References'),
      path: 'references',
      requiredPermissions: 'FtLoginPermission',
      component: CmisReferences,
    },
    {
      text: t('other', 'Other'),
      path: 'other',
      requiredPermissions: 'FtLoginPermission',
      component: OtherInfo,
    },
  ]
  if (!personId) {
    return (
      <>
        <div>{t('persons.instructions', 'Look up a person to view person data')}</div>
        <br />
        <div css={imageStyle}>
          <a
            target="_blank"
            href="https://www.churchofjesuschrist.org/media/video/2014-05-0009-christ-appears-on-the-road-to-emmaus"
            rel="noreferrer"
          >
            <Card>
              <Image src={picture} alt="Christ Appears on the Road to Emmaus" />
              <Paragraph>“Behold my Spirit is upon you…walk with me.” ~Moses 6:34</Paragraph>
            </Card>
          </a>
        </div>
      </>
    )
  }
  return (
    <>
      {currentPerson && (
        <PersonContextProvider value={personContext}>
          <RelationshipCalculatorProvider value={initialRelCalculatorContext}>
            <MetaData context="person" metadata={currentPerson.metadata} primary={currentPerson} />
            <SubnavAndRoutes data={subNavs} forward={forward} />
          </RelationshipCalculatorProvider>
        </PersonContextProvider>
      )}
    </>
  )
}
